// eslint-disable-next-line import/no-extraneous-dependencies
import { InputNumber, Segmented, Select, Tabs } from 'antd'
import moment from 'moment'
import { ToastContainer } from 'react-toastify'
import { ContainerTitle, DivTitle } from '../../components/TitlePage/TitlePage'
import { GenericTable } from '../../components/GenericTable/GenericTable'
import { Loading } from '../../components/Loading/Loading'
import { Pagination } from '../../components/Pagination/Pagination'
import { colors } from '../../theme'
import {
  CardsGrid,
  Container,
  FilterBar,
  FilterField,
  MetricCard,
  MetricHint,
  MetricTitle,
  MetricValue,
  SectionTitle,
  TableWrapper,
} from '../PredictAnalytics/PredictAnalytics.style'
import { AcaoLink, AcoesCelula } from './MixAdmin.style'
import { useTopUserData } from '../../components/Top/TopUserData/useTopUserData'
import { useMixAdmin } from './useMixAdmin'

const formatMoeda = (valor: number) =>
  (valor ?? 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  })

// Fracao 0..1 -> "73,0%". null = sem janela fechada ainda.
const formatPct = (valor: number | null | undefined) => {
  if (valor === null || valor === undefined) {
    return '—'
  }
  return `${(valor * 100).toFixed(1).replace('.', ',')}%`
}

// ISO yyyy-MM-dd -> dd/MM/yyyy. Guarda de `isValid` (idioma de centralResultados.helpers), senao
// um valor malformado vira a string "Invalid date" na celula.
const formatData = (valor: string | null) => {
  if (!valor) {
    return '—'
  }
  const data = moment(valor, 'YYYY-MM-DD')
  return data.isValid() ? data.format('DD/MM/YYYY') : '—'
}

// Opcoes dos filtros de atividade. O valor viaja como STRING e a string vazia e o "sem filtro":
// o antd trata `null`/`undefined` em option como ausencia de valor (cairia no placeholder em vez
// de mostrar o rotulo), e um sentinela numerico como `0` cairia na armadilha do teste truthy.
// A conversao para `number | null` acontece no onChange, num lugar so.
const SEM_FILTRO = ''

const OPCOES_COMPRA = [
  { value: '30', label: 'Últimos 30 dias' },
  { value: '60', label: 'Últimos 60 dias' },
  { value: '90', label: 'Últimos 90 dias' },
  { value: '180', label: 'Últimos 180 dias' },
  { value: SEM_FILTRO, label: 'Sem filtro (todos)' },
]

// "Sem filtro (todos)" fica na MESMA posicao nos dois selects (o fim), que ficam lado a lado.
const OPCOES_PREDICAO = [
  { value: '7', label: 'Próximos 7 dias' },
  { value: '15', label: 'Próximos 15 dias' },
  { value: '30', label: 'Próximos 30 dias' },
  { value: '60', label: 'Próximos 60 dias' },
  { value: SEM_FILTRO, label: 'Sem filtro (todos)' },
]

// Colunas da tabela de clientes. Array nomeado para o colSpan do estado vazio sair de `.length`
// (idioma da aba "Oportunidades") e nao de um numero fixo que a proxima coluna quebra em silencio.
const COLUNAS_CLIENTES = [
  'Cliente',
  'Vendedor',
  'Última compra',
  'Próxima predição',
  'Categorias recomendadas',
  'Potencial total',
  'Ações',
]

const paraDias = (valor: string) =>
  valor === SEM_FILTRO ? null : Number(valor)

const paraOpcao = (dias: number | null) =>
  dias === null ? SEM_FILTRO : String(dias)

// PD-0002 Mix Inteligente - palco admin em 4 abas: Oportunidades / Clientes / Simulador / Conversao.
export function MixAdmin() {
  const {
    estrategia,
    onChangeEstrategia,
    ranking,
    clientes,
    selecionada,
    selecionarCategoria,
    isLoadingRanking,
    isLoadingClientes,
    pageRanking,
    setPageRanking,
    pageClientes,
    setPageClientes,
    abaAtiva,
    setAbaAtiva,
    rankingClientes,
    isLoadingRankingClientes,
    pageRankingClientes,
    setPageRankingClientes,
    vendedorId,
    onChangeVendedor,
    listaVendedores,
    compraUltimosDias,
    onChangeCompraUltimosDias,
    predicaoProximosDias,
    onChangePredicaoProximosDias,
    categoriasDisponiveis,
    categoriasSelecionadas,
    setCategoriasSelecionadas,
    conversao,
    setConversao,
    simulacao,
    conversaoVendedor,
  } = useMixAdmin()

  const papel = useTopUserData().userDataOrganization?.papelOrganizacao
  // Quem tem escopo por vendedor (PD-0004) recebe o painel já recortado pelo
  // backend — não há filtro na tela que revele isso, então a tela diz.
  const temEscopoProprio =
    papel === 'VENDEDOR' || papel === 'ASSISTENTE' || papel === 'COORDENADOR'

  const porSegmento = estrategia === 'SEGMENTO'
  const potencialTotalMin = simulacao?.potencialTotalMin ?? 0
  const potencialTotalMax = simulacao?.potencialTotalMax ?? 0
  const receitaMin = (potencialTotalMin * conversao) / 100
  const receitaMax = (potencialTotalMax * conversao) / 100
  const rankingHeaders = porSegmento
    ? ['Categoria', 'Segmento', 'Clientes', 'Potencial total', '']
    : ['Categoria', 'Clientes', 'Potencial total', '']

  const rankingContent = Array.isArray(ranking.content) ? ranking.content : []
  const rankingClientesContent = Array.isArray(rankingClientes.content)
    ? rankingClientes.content
    : []
  // O aviso de dupla contagem so aparece quando ELA existe na pagina: no modo SEGMENTO a maioria
  // dos clientes tem um segmento so, e um aviso global valeria para a minoria.
  const temDuplaContagem = rankingClientesContent.some(
    item => item.nRecomendacoes > item.nCategorias
  )
  // Lista vazia tem causas diferentes e a mensagem precisa apontar as certas. A frase e COMPOSTA
  // com os filtros realmente ativos em vez de escolher um: como o filtro de compra vem ligado por
  // padrao, escolher "o mais provavel" faria a mensagem calar o vendedor sempre que ele tambem
  // estivesse recortando. Sem filtro nenhum, a causa e outra (o motor ainda nao rodou) e mandar o
  // usuario mexer em filtro seria empurra-lo para o lugar errado.
  const mensagemListaVazia = (() => {
    const recortes = []
    if (vendedorId !== null) {
      recortes.push('o vendedor selecionado')
    }
    if (compraUltimosDias !== null) {
      recortes.push(`compra nos últimos ${compraUltimosDias} dias`)
    }
    if (predicaoProximosDias !== null) {
      recortes.push(`predição nos próximos ${predicaoProximosDias} dias`)
    }
    if (recortes.length === 0) {
      return 'Sem oportunidades ainda (calculadas periodicamente).'
    }
    return `Nenhum cliente com oportunidade atende a: ${recortes.join(
      ', '
    )}. Amplie as janelas ou escolha “Sem filtro (todos)”.`
  })()
  const clientesContent = Array.isArray(clientes.content)
    ? clientes.content
    : []
  const chaveSelecionada = selecionada
    ? `${selecionada.categoria}::${selecionada.segmento}`
    : ''

  // Aba "Oportunidades": ranking por categoria + consulta invertida inline (ao clicar numa categoria).
  const abaOportunidades = (
    <>
      <SectionTitle>Oportunidades por categoria</SectionTitle>
      {porSegmento && (
        <MetricHint>
          Por segmento, a mesma categoria aparece uma vez para cada segmento em
          que é recomendada — Clientes e Potencial total são daquele segmento,
          não da categoria inteira.
        </MetricHint>
      )}
      <TableWrapper>
        <GenericTable
          font="14px"
          spacing="16px"
          vendas
          headers={rankingHeaders}
        >
          {isLoadingRanking ? (
            <Loading contrast={false} />
          ) : (
            <>
              {rankingContent.map(item => {
                const chave = `${item.categoria}::${item.segmento}`
                const potencial = `${formatMoeda(
                  item.potencialTotalMin
                )} – ${formatMoeda(item.potencialTotalMax)}`
                return (
                  <tr
                    key={chave}
                    style={{
                      cursor: 'pointer',
                      fontWeight: chaveSelecionada === chave ? 700 : 400,
                    }}
                    onClick={() =>
                      selecionarCategoria(item.categoria, item.segmento)
                    }
                  >
                    <td>{item.categoria}</td>
                    {porSegmento && <td>{item.segmento}</td>}
                    <td>{item.nClientes}</td>
                    <td>{potencial}</td>
                    <td>Ver clientes</td>
                  </tr>
                )
              })}
              {Array.isArray(ranking.content) &&
                rankingContent.length === 0 && (
                  <tr>
                    <td colSpan={rankingHeaders.length}>
                      Sem oportunidades ainda (calculadas periodicamente).
                    </td>
                  </tr>
                )}
            </>
          )}
        </GenericTable>
      </TableWrapper>
      {ranking && ranking.totalElements > 0 && (
        <Pagination
          pageNumber={pageRanking}
          pageSize={ranking.totalElements}
          offSet={ranking.size}
          onClick={page => setPageRanking(page)}
        />
      )}

      {selecionada && (
        <>
          <SectionTitle>
            Clientes com potencial de compra de: {selecionada.categoria}
          </SectionTitle>
          <TableWrapper>
            <GenericTable
              font="14px"
              spacing="16px"
              vendas
              headers={['Cliente', 'Vendedor', 'Potencial', 'Posição']}
            >
              {isLoadingClientes ? (
                <Loading contrast={false} />
              ) : (
                <>
                  {clientesContent.map(item => {
                    const faixa = `${formatMoeda(
                      item.potencialMin
                    )} – ${formatMoeda(item.potencialMax)}`
                    return (
                      <tr key={item.clienteId}>
                        <td>{item.clienteNome}</td>
                        <td>{item.vendedorNome || 'Sem vendedor'}</td>
                        <td>{faixa}</td>
                        <td>{item.posicao}</td>
                      </tr>
                    )
                  })}
                  {Array.isArray(clientes.content) &&
                    clientesContent.length === 0 && (
                      <tr>
                        <td colSpan={4}>Nenhum cliente para esta categoria.</td>
                      </tr>
                    )}
                </>
              )}
            </GenericTable>
          </TableWrapper>
          {clientes && clientes.totalElements > 0 && (
            <Pagination
              pageNumber={pageClientes}
              pageSize={clientes.totalElements}
              offSet={clientes.size}
              onClick={page => setPageClientes(page)}
            />
          )}
        </>
      )}
    </>
  )

  // Aba "Simulador": projecao de campanha (categorias + conversao esperada).
  const abaSimulador = (
    <>
      <FilterBar>
        <FilterField>
          <span>Categorias</span>
          <Select
            mode="multiple"
            allowClear
            style={{ minWidth: 320 }}
            placeholder="Selecione categorias para trabalhar"
            optionFilterProp="label"
            value={categoriasSelecionadas}
            onChange={valores => setCategoriasSelecionadas(valores)}
            options={categoriasDisponiveis.map(categoria => ({
              value: categoria,
              label: categoria,
            }))}
          />
        </FilterField>
        <FilterField>
          <span>Conversão esperada</span>
          <InputNumber
            min={0}
            max={100}
            value={conversao}
            onChange={valor => setConversao(valor ?? 0)}
            addonAfter="%"
          />
        </FilterField>
      </FilterBar>

      {categoriasSelecionadas.length === 0 ? (
        <MetricHint>
          Selecione uma ou mais categorias para projetar a campanha.
        </MetricHint>
      ) : (
        <CardsGrid>
          <MetricCard>
            <MetricTitle>Clientes alcançados</MetricTitle>
            <MetricValue>
              {(simulacao?.clientesAlcancados ?? 0).toLocaleString('pt-BR')}
            </MetricValue>
            <MetricHint>
              {simulacao?.nCategorias ?? 0} categoria(s) selecionada(s)
            </MetricHint>
          </MetricCard>
          <MetricCard>
            <MetricTitle>Potencial total</MetricTitle>
            <MetricValue>
              {formatMoeda(potencialTotalMin)} –{' '}
              {formatMoeda(potencialTotalMax)}
            </MetricValue>
            <MetricHint>
              {porSegmento
                ? 'Oportunidade histórica no período. Clientes alcançados conta cada cliente uma vez; o potencial soma linha a linha, então o cliente em dois segmentos entra duas vezes na mesma categoria.'
                : 'Oportunidade histórica no período'}
            </MetricHint>
          </MetricCard>
          <MetricCard>
            <MetricTitle>Receita projetada</MetricTitle>
            <MetricValue>
              {formatMoeda(receitaMin)} – {formatMoeda(receitaMax)}
            </MetricValue>
            <MetricHint>A {conversao}% de conversão</MetricHint>
          </MetricCard>
        </CardsGrid>
      )}
    </>
  )

  // Aba "Conversao": conversao do vendedor (nivel 2 da medicao).
  const abaConversao = (
    <CardsGrid>
      <MetricCard>
        <MetricTitle>Recomendações trabalhadas que viraram compra</MetricTitle>
        <MetricValue>{formatPct(conversaoVendedor?.taxaConversao)}</MetricValue>
        {conversaoVendedor && conversaoVendedor.totalTrabalhadas > 0 ? (
          <MetricHint>
            {conversaoVendedor.convertidas} de{' '}
            {conversaoVendedor.totalTrabalhadas} marcadas como úteis viraram
            compra em 60 dias
          </MetricHint>
        ) : (
          <MetricHint>
            Sem dados ainda: aguarda a janela de 60 dias e acumula desde a
            ativação.
          </MetricHint>
        )}
      </MetricCard>
    </CardsGrid>
  )

  // Aba "Clientes": onde ha mais valor a agregar. Mesmo agregado do ranking de categorias, com o
  // eixo invertido (cliente), mais o filtro pelo vendedor atribuido.
  const abaClientes = (
    <>
      <FilterBar>
        <FilterField>
          <span>Vendedor</span>
          <Select
            allowClear
            showSearch
            style={{ width: 220 }}
            aria-label="Vendedor"
            placeholder={
              temEscopoProprio ? 'Todos os meus' : 'Todos os vendedores'
            }
            optionFilterProp="label"
            value={vendedorId === null ? undefined : vendedorId}
            onChange={valor => onChangeVendedor(valor ?? null)}
            options={listaVendedores.map(item => ({
              value: item.id,
              label: item.nome,
            }))}
          />
        </FilterField>
        <FilterField>
          <span>Compra recente</span>
          <Select
            style={{ width: 200 }}
            aria-label="Compra recente"
            value={paraOpcao(compraUltimosDias)}
            onChange={valor => onChangeCompraUltimosDias(paraDias(valor))}
            options={OPCOES_COMPRA}
          />
        </FilterField>
        <FilterField>
          <span>Predição a caminho</span>
          <Select
            style={{ width: 200 }}
            aria-label="Predição a caminho"
            value={paraOpcao(predicaoProximosDias)}
            onChange={valor => onChangePredicaoProximosDias(paraDias(valor))}
            options={OPCOES_PREDICAO}
          />
        </FilterField>
      </FilterBar>

      <SectionTitle>Clientes com maior potencial a agregar</SectionTitle>
      <MetricHint>
        Soma, por cliente, do valor que os clientes que já compram cada
        categoria recomendada costumam gastar nela (faixa p25–p75). Mede o
        tamanho das categorias que faltam ao cliente, não o porte dele.
      </MetricHint>
      {vendedorId !== null && (
        <MetricHint>
          O vendedor vem da venda mais recente do cliente: quem ainda não tem
          histórico de compra fica de fora do filtro.
        </MetricHint>
      )}
      <MetricHint>
        &quot;Última compra&quot; é a venda mais recente no histórico disponível
        e &quot;Próxima predição&quot; é a menor data prevista de hoje em diante
        ainda não baixada. Um traço significa que não há esse registro —
        predição já vencida não aparece nessa coluna.
      </MetricHint>
      {compraUltimosDias !== null && (
        <MetricHint>
          Mostrando só quem comprou nos últimos {compraUltimosDias} dias. Isso
          deixa de fora, além dos clientes parados, todos os que não têm compra
          no histórico disponível — que são a maioria das recomendações por
          abertura de segmento. Escolha &quot;Sem filtro (todos)&quot; para
          vê-los.
        </MetricHint>
      )}
      {predicaoProximosDias !== null && (
        <MetricHint>
          Mostrando só quem tem predição prevista de hoje até{' '}
          {predicaoProximosDias} dias à frente e ainda não baixada — mesma
          definição da tela de Predições. O total não é o mesmo de lá: aquela
          tela usa o período e o vendedor escolhidos nela.
        </MetricHint>
      )}
      {temDuplaContagem && (
        <MetricHint>
          Nesta página há cliente que pertence a mais de um segmento e recebeu a
          mesma categoria por dois deles. Onde o número de recomendações supera
          o de categorias, essa categoria entra duas vezes no potencial — e a
          ordenação da lista também sente isso.
        </MetricHint>
      )}
      <TableWrapper>
        <GenericTable
          font="14px"
          spacing="16px"
          vendas
          headers={COLUNAS_CLIENTES}
        >
          {isLoadingRankingClientes ? (
            <Loading contrast={false} />
          ) : (
            <>
              {rankingClientesContent.map(item => (
                <tr key={item.clienteId}>
                  <td>{item.clienteNome}</td>
                  <td>{item.vendedorNome || 'Sem vendedor'}</td>
                  <td>{formatData(item.dataUltimaCompra)}</td>
                  <td>{formatData(item.dataProximaPredicao)}</td>
                  <td>
                    {item.nCategorias}
                    {item.nRecomendacoes > item.nCategorias
                      ? ` (${item.nRecomendacoes} recomendações)`
                      : ''}
                  </td>
                  <td>
                    {formatMoeda(item.potencialTotalMin)} –{' '}
                    {formatMoeda(item.potencialTotalMax)}
                  </td>
                  <td>
                    <AcoesCelula>
                      <AcaoLink
                        href={`/predicao/${item.clienteId}`}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Ver predição de ${item.clienteNome} (abre em nova aba)`}
                        title={`Abrir a predição de ${item.clienteNome} em nova aba`}
                      >
                        Ver predição
                      </AcaoLink>
                      <AcaoLink
                        href={`/clientes/${item.clienteId}`}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Ver cliente ${item.clienteNome} (abre em nova aba)`}
                        title={`Abrir a ficha de ${item.clienteNome} em nova aba`}
                      >
                        Ver cliente
                      </AcaoLink>
                    </AcoesCelula>
                  </td>
                </tr>
              ))}
              {Array.isArray(rankingClientes.content) &&
                rankingClientesContent.length === 0 && (
                  <tr>
                    <td colSpan={COLUNAS_CLIENTES.length}>
                      {mensagemListaVazia}
                    </td>
                  </tr>
                )}
            </>
          )}
        </GenericTable>
      </TableWrapper>
      {rankingClientes && rankingClientes.totalElements > 0 && (
        <Pagination
          pageNumber={pageRankingClientes}
          pageSize={rankingClientes.totalElements}
          offSet={rankingClientes.size}
          onClick={page => setPageRankingClientes(page)}
          label="clientes"
        />
      )}
    </>
  )

  return (
    <Container>
      <ToastContainer theme="dark" />
      <ContainerTitle>
        <DivTitle color={colors.primaryText} size={24}>
          Mix Inteligente
        </DivTitle>
      </ContainerTitle>

      {temEscopoProprio && (
        <MetricHint>
          Este painel mostra <strong>só a sua carteira</strong> — as
          recomendações atribuídas a você (ou à sua equipe). A atribuição é a do
          vendedor da venda mais recente do cliente, então{' '}
          <strong>cliente que ainda não comprou não aparece aqui</strong>: ele
          não tem vendedor atribuído. Quem precisa da visão completa da
          organização é o administrador.
        </MetricHint>
      )}

      <FilterBar>
        <FilterField>
          <span>Estratégia</span>
          <Segmented
            value={estrategia}
            onChange={valor => onChangeEstrategia(valor as MixEstrategiaType)}
            options={[
              { label: 'Por similaridade', value: 'JACCARD' },
              { label: 'Por segmento', value: 'SEGMENTO' },
            ]}
          />
        </FilterField>
      </FilterBar>

      <Tabs
        activeKey={abaAtiva}
        onChange={setAbaAtiva}
        items={[
          {
            key: 'oportunidades',
            label: 'Oportunidades',
            children: abaOportunidades,
          },
          {
            key: 'clientes',
            label: 'Clientes por potencial',
            children: abaClientes,
          },
          { key: 'simulador', label: 'Simulador', children: abaSimulador },
          { key: 'conversao', label: 'Conversão', children: abaConversao },
        ]}
      />
    </Container>
  )
}
